'use client';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type PlatformTab = 'ios' | 'android';

const DISMISSED_KEY = 'pwa-install-dismissed';

function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /Mobi|Android|iPhone|iPad|iPod/i.test(ua) || window.matchMedia('(pointer: coarse)').matches
  );
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

function detectPlatform(): PlatformTab {
  if (typeof window === 'undefined') return 'android';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios';
  return 'android';
}

export default function PWAInstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [platform, setPlatform] = useState<PlatformTab>('android');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISSED_KEY)) return;

    if (isMobileDevice()) {
      setShowBanner(true);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (!showGuide) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowGuide(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [showGuide]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
      setShowGuide(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(DISMISSED_KEY, '1');
    }
  };

  const openGuide = () => {
    setPlatform(detectPlatform());
    setShowGuide(true);
  };

  const iosSteps = [t('pwaIosStep1'), t('pwaIosStep2'), t('pwaIosStep3'), t('pwaIosStep4')];
  const androidSteps = [
    t('pwaAndroidStep1'),
    t('pwaAndroidStep2'),
    t('pwaAndroidStep3'),
    t('pwaAndroidStep4'),
  ];
  const steps = platform === 'ios' ? iosSteps : androidSteps;

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-20 left-0 right-0 z-50 px-4 max-w-md mx-auto"
          >
            <div
              className="flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl"
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border)',
                backdropFilter: 'blur(16px)',
              }}
            >
              <button
                type="button"
                onClick={openGuide}
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all active:scale-90"
                style={{ background: 'var(--primary)' }}
                aria-label={t('pwaHowToTitle')}
              >
                <Download size={18} style={{ color: 'var(--primary-foreground)' }} />
              </button>
              <button type="button" onClick={openGuide} className="flex-1 min-w-0 text-start">
                <p className="text-sm font-bold text-foreground">{t('pwaInstallTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('pwaInstallMsg')}</p>
              </button>
              {deferredPrompt && (
                <button
                  type="button"
                  onClick={handleInstall}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95 flex-shrink-0"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  {t('pwaInstall')}
                </button>
              )}
              <button
                type="button"
                onClick={handleDismiss}
                className="px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95 flex-shrink-0"
                style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
              >
                {t('pwaDismiss')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-end justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowGuide(false)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="w-full max-w-sm rounded-3xl overflow-hidden"
              style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="font-bold text-base text-foreground">{t('pwaHowToTitle')}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('pwaHowToSubtitle')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowGuide(false)}
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all active:scale-90"
                    style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                    aria-label={t('cancel')}
                  >
                    <X size={16} />
                  </button>
                </div>

                <div
                  className="grid grid-cols-2 p-1 rounded-2xl mb-4"
                  style={{ background: 'var(--muted)' }}
                >
                  {(['ios', 'android'] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setPlatform(tab)}
                      className="py-2 rounded-xl text-xs font-bold transition-all active:scale-95"
                      style={
                        platform === tab
                          ? {
                              background: 'var(--card)',
                              color: 'var(--foreground)',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            }
                          : { background: 'transparent', color: 'var(--muted-foreground)' }
                      }
                    >
                      {tab === 'ios' ? t('pwaIos') : t('pwaAndroid')}
                    </button>
                  ))}
                </div>

                <ol className="flex flex-col gap-3 mb-5">
                  {steps.map((step, index) => (
                    <li key={`${platform}-${index}`} className="flex items-start gap-3">
                      <div
                        className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold"
                        style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                      >
                        {index + 1}
                      </div>
                      <p className="text-sm text-foreground leading-snug pt-1.5 min-w-0">{step}</p>
                    </li>
                  ))}
                </ol>

                <button
                  type="button"
                  onClick={() => setShowGuide(false)}
                  className="w-full py-3 rounded-2xl text-sm font-bold transition-all active:scale-95"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  {t('pwaGotIt')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
